"use client";
import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaCarouselType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import Button from "../atoms/Button";
import { useBannerStore } from "../../store/bannerStore";
import PageLoader from "../../components/atoms/PageLoader";

export default function Banner() {
  const { banners, fetchBanners } = useBannerStore();
  const [loading, setLoading] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const location = useLocation();

  // 🔹 Tentukan bahasa dari URL
  const currentLang = location.pathname.startsWith("/id") ? "id" : "en";

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
        setLoading(true);
    fetchBanners(currentLang).finally(() => setLoading(false));
  }, [fetchBanners, currentLang]);

  useEffect(() => {
    if (emblaApi) {
      onSelect(emblaApi);
      emblaApi.on("select", onSelect);
    }
  }, [emblaApi, onSelect]);

  const withLang = (url: string) => {
    if (!url.startsWith("/")) return url;
    return `/${currentLang}${url}`;
  };

  return (
    <section className="relative w-full h-screen overflow-hidden">
      
    {loading && <PageLoader />}
      <div className="embla h-full" ref={emblaRef}>
        <div className="embla__container h-full flex">
          {banners.map((banner) => (
            <div
              key={banner.id}
              className="embla__slide flex-[0_0_100%] relative h-full bg-cover bg-center"
            >
              <div
                className="absolute inset-0 bg-center bg-cover hidden md:block"
                style={{ backgroundImage: `url(${banner.image_desktop})` }}
              />
              <div
                className="absolute inset-0 bg-center bg-cover md:hidden"
                style={{ backgroundImage: `url(${banner.image_mobile})` }}
              />
              <div className="absolute inset-0 bg-black/50" />

              <div className="relative z-10 h-full flex flex-col justify-center text-white container-banner sm:px-10 lg:px-20 xl:px-28 max-w-[1920px] mx-auto">
                <div className="flex-1 flex flex-col justify-center sm:pt-10 lg:pt-14 xl:pt-14">
                  <h2 className="text-3xl md:text-5xl font-medium mb-4 md:w-3/4 lg:w-2/3 text-shadow">
                    {banner.title}
                  </h2>
                  <p className="text-base md:text-xl font-light mb-8 w-full md:w-3/4 lg:w-2/3 text-shadow">
                    {banner.sub_title}
                  </p>
                </div>

                <div className="flex justify-between items-center pb-24">
                  <Button
                    href={withLang(banner.button_url)}
                    bgClass="bg-primary"
                    hoverBgClass="hover:bg-secondary"
                    textClass="text-font-primary"
                    hoverTextClass="hover:text-font-secondary"
                    className="shadow-lg px-6 py-3"
                  >
                    {currentLang === "id" ? "Selengkapnya" : "Learn More"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bullet indicator */}
      <div className="absolute bottom-24 right-4 md:right-32 z-20 w-full">
        <div className="max-w-[1920px] mx-auto flex justify-end space-x-2">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              style={{
                backgroundColor: "#ffec89",
                transform: i === selectedIndex ? "scale(1.75)" : "scale(1)",
                opacity: i === selectedIndex ? "1" : "0.5",
              }}
              className="w-3 h-3 rounded-full transition-transform duration-300"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
